import {
  Button,
  DropdownMenu,
  Icon,
  SvgIconNames,
} from '@toggle/design-system';
import React, { RefObject, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Option } from '~/components/filter/Option';
import { useReasonerParams } from '~/views/alfred/research/hooks/useReasonerParams';
import { useUploadDisabled } from '~/views/alfred/research/hooks/useUploadDisabled';
import { useUserUpload } from '~/views/alfred/research/use-user-upload/useUserUpload';
import {
  allowedTypesExtension,
  formatUploadFiles,
} from '~/views/alfred/research/utils/formatUploadFiles';

import styles from './UploadDropdown.module.scss';

interface DropdownOption {
  key: string;
  icon: SvgIconNames;
  name: string;
  value: {
    upload_type: 'files';
  };
}

export interface UploadDropdownProps {
  contentRef?: RefObject<HTMLDivElement | null>;
}

export const UploadDropdown = ({ contentRef }: UploadDropdownProps) => {
  const { t } = useTranslation(['chat']);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { conversationId } = useReasonerParams();

  const { disabled } = useUploadDisabled(conversationId);
  const addFiles = useUserUpload(state => state.addFiles);

  const options: DropdownOption[] = [
    {
      key: 'addFiles',
      icon: 'Paperclip',
      name: t('chat:userUpload.addFiles'),
      value: {
        upload_type: 'files',
      },
    },
  ];

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }

    const files = event.target.files;

    if (files && files.length > 0) {
      addFiles(formatUploadFiles(Array.from(files)));

      // Reset the input so the same file can be selected again
      event.target.value = '';
    }
  };

  const handleOptionClick = (optionKey: string) => {
    if (optionKey === 'addFiles') {
      handleFileClick();
    } else if (optionKey === 'addUrls') {
      // TODO: [REASON-851] Handle URL input logic
    }
  };

  return (
    <>
      <input
        className={styles.styledInput}
        ref={fileInputRef}
        type="file"
        accept={allowedTypesExtension}
        multiple
        disabled={disabled}
        onChange={handleFileChange}
      />
      <DropdownMenu
        className={styles.styledDropdownMenu}
        contentRef={contentRef}
        triggerProps={{
          disabled,
        }}
        triggerNode={
          <div>
            <Button
              className={styles.styledButton}
              variant="empty"
              iconName="Add"
              size="small"
              disabled={disabled}
            />
          </div>
        }
      >
        {options.map(i => {
          return (
            <Option
              key={i.key}
              option={i}
              indentedDescription={24}
              handleClick={() => handleOptionClick(i.key)}
              renderCustomFilterOptionLabel={() => {
                return (
                  <div className={styles.optionWrapper}>
                    <Icon iconName={i.icon} size={16} />
                    <span className={styles.label}>{i.name}</span>
                  </div>
                );
              }}
            />
          );
        })}
      </DropdownMenu>
    </>
  );
};
